import styled from "@emotion/styled";
import { Modal, Typography } from "@mui/material";
import { CSSProperties, useEffect, useState } from "react";
import { Bars } from "react-loader-spinner";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import ConfirmationModal from "../shared/components/ConfirmationModal";
import zIndicies from "../shared/utils/zIndexConstants";
import { AppColors } from "../theme/AppTheme";
import MemoryRow from "./components/MemoryRow";
import ProfileSectionHeader from "./components/ProfileSectionHeader";
import AccessCode from "./models/AccessCode";
import AccessCodeService from "../services/accessCodeService";
import CodeRow from "./components/CodeRow";


const ScrollingWrapper = styled('div')(() => ({
    overflowY: 'auto',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
}));

const StyledContainer = styled('div')((props: { style?: CSSProperties, isMobile: boolean }) => ({
    padding: props.isMobile ? '24px' : '50px',
    maxWidth: '1200px',
    width: props.isMobile ? 'calc(100% - 48px)' : '100%',
    ...props.style,
}));



const CodeManagementPage = () => {
    const isMobile = useAppSelector(store => store.appState.isMobile);

    const [codes, setCodes] = useState<AccessCode[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteCode, setDeleteCode] = useState<AccessCode | undefined>();

    useEffect(() => {
        loadCodes();
    }, []);

    const loadCodes = async () => {
        const fetchedCodes = await AccessCodeService.fetchCodes();
        setCodes(fetchedCodes);
        setLoading(false);
    }

    const performDeleteCode = async () => {
        if (!deleteCode) return;
        const codeToDelete = deleteCode;
        setCodes(codes.filter((code) => code.id !== deleteCode.id))
        setDeleteCode(undefined);
        await AccessCodeService.deleteCode(codeToDelete);
    }

    const codeWasUpdated = (updatedCode: AccessCode) => {
        setCodes(codes.map((code) => {
            if (code.id === updatedCode.id) {
                return updatedCode;
            }
            return code;
        }))
    }

    const newCodeCreated = (index: number, newCode: AccessCode) => {
        setCodes(codes.map((code, i) => {
            if (i === index) {
                return newCode;
            }
            return code;
        }))
    }

    const onAddNewCodeClicked = async () => {
        setCodes([...codes, { id: '', name: '', code: '' }]);
    }

    const clearUnsavedCode = (index: number) => {
        setCodes(codes.filter((_, i) => i !== index));
    }

    return (
        <ScrollingWrapper>
            <StyledContainer isMobile={isMobile}>
                <ProfileSectionHeader title='Manage Access Codes' />

                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', marginTop: '40px', flexDirection: `${isMobile ? 'column' : 'row'}` }}>
                    <Typography variant="h4" style={{ textAlign: 'left', width: '200px', minWidth: '200px' }}>
                        Acces Codes
                    </Typography>
                </div>
                {<div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', marginTop: '28px', flexDirection: `${isMobile ? 'column' : 'row'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', color: AppColors.grey.darkish, width: '100%' }}>
                        {loading && <Bars
                            height="30"
                            width="30"
                            color={AppColors.pink.dark}
                            ariaLabel="bars-loading"
                            visible={true}
                        />}
                        {!loading && codes.length === 0 &&
                            <Typography variant='h3' style={{ marginTop: '0px', color: AppColors.grey.darkish }}>
                                No access codes
                            </Typography>
                        }
                        {!loading && codes.length > 0 && <div style={{ marginTop: '0px', width: '100%' }}>
                            {codes.map((code, index) =>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <CodeRow key={`${index}`} code={code} onDeleteClicked={(code) => setDeleteCode(code)} onCodeUpdated={codeWasUpdated} onCodeAdded={(newCode => newCodeCreated(index, newCode))} clearUnsavedCode={() => clearUnsavedCode(index)} />
                                    <div style={{ marginLeft: '1%', width: '98%' }}>
                                        <div style={{ width: '100%', minHeight: '1.5px', backgroundColor: AppColors.separator }} />
                                    </div>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '20px' }}>
                                <button
                                    onClick={onAddNewCodeClicked}
                                    style={{
                                        backgroundColor: AppColors.pink.dark,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    +
                                </button>
                            </div>
                        </div>}
                    </div>
                </div>}
                {loading && <div style={{ minHeight: '40px', maxHeight: '40px', }} />}

                <div style={{ minHeight: '20px', maxHeight: '20px', }} />
            </StyledContainer>
            <Modal
                sx={{ zIndex: zIndicies.modal }}
                open={!!deleteCode}>
                <ConfirmationModal title="Delete Access Code" subtitle={`Remove access code: \"${deleteCode?.code}\"`} onCancel={() => { setDeleteCode(undefined) }} onConfirm={performDeleteCode} confirmText="Yes" />
            </Modal>
        </ScrollingWrapper>
    )
}

export default CodeManagementPage;